import {Button} from "primereact/button";
import {Menu} from "primereact/menu";
import type {MenuItem, MenuItemOptions} from "primereact/menuitem";
import type {ReactNode} from "react";
import {useRef} from "react";
import {useTranslation} from "react-i18next";

import {openProfileDialog, selectUserProfileEditable} from "../../redux/accountManagementSlice";
import {fetchUserProfileThunk} from "../../redux/accountManagementThunk";
import {clearAddress} from "../../redux/addressSlice";
import {selectFullNameOrUsername, selectLicenseDisplayNameOrName} from "../../redux/authSelectors";
import {selectUsername} from "../../redux/authSlice";
import {resetDocuments} from "../../redux/documentsSlice";
import {performLogout} from "../../redux/loginThunk";
import {resetNotifications} from "../../redux/notificationsSlice";
import {resetPersistence} from "../../redux/persistenceSlice";
import {useRevaDispatch, useRevaSelector} from "../../redux/revaHooks";
import {resetValuations} from "../../redux/searchValuationsSlice";
import {resetValueOrders} from "../../redux/valueOrdersSlice";
import ProfileDialog from "./ProfileDialog";

interface HeaderUserMenuProps {
    onLogoutCallback?: () => void;
}

/**
 * User Menu (and Button) in the Header.
 */
export default function HeaderUserMenu({onLogoutCallback}: HeaderUserMenuProps) {
    const {t} = useTranslation();

    const dispatch = useRevaDispatch();

    const userFullName = useRevaSelector(selectFullNameOrUsername);
    const username = useRevaSelector(selectUsername);
    const license = useRevaSelector(selectLicenseDisplayNameOrName);
    // #19351: GET /user/profile decides whether "Profil bearbeiten" is usable; while unknown (not loaded
    // yet) or unreachable/forbidden this selector reports false — see accountManagementSlice.
    const profileEditable = useRevaSelector(selectUserProfileEditable);

    const menuRef = useRef<Menu | null>(null);

    // #19351: load fresh account-management data every time the menu opens (Menu.show() calls onShow
    // synchronously — see primereact/menu — so this is not a stale-by-one-open value).
    const onMenuShow = () => {
        void dispatch(fetchUserProfileThunk());
    };

    const onLogoutClick = () => {
        void dispatch(performLogout());

        dispatch(clearAddress());
        dispatch(resetPersistence());
        dispatch(resetDocuments());
        dispatch(resetNotifications());
        dispatch(resetValuations());
        dispatch(resetValueOrders());

        if (typeof onLogoutCallback === "function") {
            onLogoutCallback();
        }
    };

    const unavailableTooltip = t("header.userMenu.accountManagementUnavailable");

    /**
     * #19351: renders "Profil bearbeiten" as a fully custom item (not relying on PrimeReact's own
     * `disabled` styling, whose pointer-events handling is opaque) — greyed out and inert while account
     * management is unreachable/forbidden, but still hoverable for the tooltip. A template replaces the whole
     * `p-menuitem-content` wrapper, so it has to render that div itself — the theme's item padding hangs on it.
     */
    const accountMenuItemTemplate =
        (onClick: () => void) =>
        (item: MenuItem, options: MenuItemOptions): ReactNode => (
            <div className="p-menuitem-content">
                <a
                    className={`${options.className} ${profileEditable ? "" : "opacity-50 cursor-not-allowed"}`}
                    style={{pointerEvents: "auto"}}
                    test-key={item.id}
                    title={profileEditable ? undefined : unavailableTooltip}
                    aria-disabled={!profileEditable}
                    onClick={profileEditable ? onClick : undefined}
                >
                    <span className={options.iconClassName} aria-hidden />
                    <span className={options.labelClassName}>{item.label}</span>
                </a>
            </div>
        );

    const menuItems: MenuItem[] = [
        {
            id: "userMenuHintUsername",
            label: t("header.userMenu.username", {username}),
            disabled: true,
            visible: userFullName !== username,
        },
        {
            id: "userMenuHintLicense",
            label: t("header.userMenu.license", {license}),
            disabled: true,
        },
        {
            separator: true,
        },
        {
            id: "userMenuProfileLink",
            label: t("header.userMenu.editProfile"),
            icon: "pi pi-user-edit",
            disabled: !profileEditable,
            template: accountMenuItemTemplate(() => dispatch(openProfileDialog())),
        },
        {
            separator: true,
        },
        {
            id: "userMenuLogout",
            label: t("header.userMenu.logout"),
            icon: "pi pi-sign-out",
            command: onLogoutClick,
        },
    ];

    return (
        <>
            <Menu
                id="headerUserMenu"
                ref={menuRef}
                model={menuItems}
                popup
                popupAlignment="right"
                onShow={onMenuShow}
                style={{width: "22rem", fontSize: "0.9rem"}}
            />
            <Button
                id="headerUserMenuBtn"
                icon="pi pi-user"
                size="small"
                onClick={(event) => menuRef.current?.toggle(event)}
                label={userFullName}
                text
                aria-controls="headerUserMenu"
                aria-haspopup
            />
            <ProfileDialog />
        </>
    );
}
